/* R60 PDP motion (file name kept from R58 so the dispatcher needs no change).
   1. Science stage: a luminous, product-tinted living cell on deep navy, drawn AROUND the centred molecule illustration
      (homepage R47 cell material). NMN: NAD+ light flows in. Resveratrol: a ring of light circles. Spermidine: vesicles
      carry debris inward. Fisetin: grey older cells fade outside. Hair: fine strands sway. Decorative only: no data.
   2. Journey: each milestone fades and rises into view (600 ms, staggered); the rail fills in the product colour.
   Text never moves after its entrance. No controls. One shared loop at 30 fps, started near the viewport, stopped
   offscreen and in a hidden tab. Reduced motion: one complete still frame, and the journey is shown at once. */
(() => {
  if (customElements.get('masi-pdp-r58-cell')) return;
  const TAU = Math.PI * 2, FPS = 30, LOOP = 60;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const script = document.currentScript;

  // Stylesheet fallback: the journey snippet links masi-pdp-r60-motion.css; other PDPs get it here until the dispatcher links it.
  const ensureCss = () => {
    if (document.querySelector('link[href*="masi-pdp-r60-motion.css"]') || !script?.src) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = script.src.replace(/masi-pdp-r58-motion\.js[^/]*$/, 'masi-pdp-r60-motion.css');
    document.head.appendChild(link);
  };

  const rng = seed => () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
  const rgb = hex => { const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim()); const n = parseInt(m ? m[1] : '71ebff', 16); return [n >> 16 & 255, n >> 8 & 255, n & 255]; };
  const col = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
  const mix = (c, d, k) => c.map((v, i) => Math.round(v + (d[i] - v) * k));
  const WHITE = [255, 255, 255], GREY = [176, 188, 198];

  // Glowing sprite and pearl organelle, drawn once per colour.
  function glowDot(size, tint) {
    const c = document.createElement('canvas'); c.width = c.height = size;
    const g = c.getContext('2d'), r = size / 2, grad = g.createRadialGradient(r, r, 0, r, r, r);
    grad.addColorStop(0, col(WHITE, 1)); grad.addColorStop(.22, col(mix(tint, WHITE, .4), .95)); grad.addColorStop(.55, col(tint, .35)); grad.addColorStop(1, col(tint, 0));
    g.fillStyle = grad; g.fillRect(0, 0, size, size); return c;
  }
  function pearl(size, tint) {
    const c = document.createElement('canvas'); c.width = c.height = size;
    const g = c.getContext('2d'), r = size / 2;
    const body = g.createRadialGradient(r * .68, r * .6, r * .05, r, r, r);
    body.addColorStop(0, col(WHITE, .95)); body.addColorStop(.35, col(mix(tint, WHITE, .3), .75)); body.addColorStop(.85, col(tint, .45)); body.addColorStop(1, col(tint, .1));
    g.fillStyle = body; g.beginPath(); g.arc(r, r, r * .96, 0, TAU); g.fill();
    g.strokeStyle = col(WHITE, .55); g.lineWidth = size * .04; g.beginPath(); g.arc(r, r, r * .9, 0, TAU); g.stroke();
    return c;
  }

  // Product behaviour. Every motion is a whole number of cycles per LOOP, so the loop is seamless.
  // The molecule covers the inner ~0.55 R, so activity lives between 0.6 R and the membrane, or just outside it.
  const INNER = {
    nad(ctx, cx, cy, R, ph, e) {
      for (const s of e.items) {
        const p = (s.o + ph / TAU * 2) % 1, rad = R * (1.02 - .42 * p), a = s.a + p * .9, size = s.z * (1.3 - .5 * p);
        ctx.globalAlpha = Math.sin(Math.PI * p);
        ctx.drawImage(e.spark, cx + rad * Math.cos(a) - size, cy + rad * Math.sin(a) - size, size * 2, size * 2);
      }
      ctx.globalAlpha = 1;
    },
    sirt1(ctx, cx, cy, R, ph, e) {
      for (let i = 0; i < 48; i++) {
        const a = i / 48 * TAU + ph, glow = Math.pow(.5 + .5 * Math.sin(i / 48 * TAU * 4 - ph * 8), 2);
        const x = cx + R * .8 * Math.cos(a), y = cy + R * .8 * Math.sin(a), size = R * (.018 + .03 * glow);
        ctx.globalAlpha = .35 + .65 * glow; ctx.drawImage(e.spark, x - size, y - size, size * 2, size * 2);
      }
      ctx.globalAlpha = 1;
    },
    autophagy(ctx, cx, cy, R, ph, e) {
      for (const s of e.items.slice(0, 12)) {
        const p = (s.o + ph / TAU) % 1, rad = R * (.95 - .3 * p), a = s.a + ph, fade = Math.sin(Math.PI * p);
        const x = cx + rad * Math.cos(a), y = cy + rad * Math.sin(a), v = R * .16;
        ctx.globalAlpha = .9 * fade; ctx.drawImage(e.vesicle, x - v / 2, y - v / 2, v, v);
        ctx.globalAlpha = (1 - p) * fade; ctx.fillStyle = col(GREY, 1);
        ctx.beginPath(); ctx.arc(x + v * .15, y - v * .1, R * .018, 0, TAU); ctx.fill();
      }
      ctx.globalAlpha = 1;
    },
    senescent(ctx, cx, cy, R, ph, e) {
      for (const s of e.items.slice(0, 8)) {
        const p = (s.o + ph / TAU) % 1, a = s.a + ph, rad = R * 1.12, k = Math.sin(Math.PI * p);
        const x = cx + rad * Math.cos(a), y = cy + rad * Math.sin(a), r = R * .1 * (1 - .45 * p);
        ctx.fillStyle = col(GREY, .22 * k); ctx.strokeStyle = col(GREY, .6 * k); ctx.lineWidth = 1.2;
        ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.fill(); ctx.stroke();
      }
    },
    follicle(ctx, cx, cy, R, ph, e) {
      ctx.lineCap = 'round';
      for (let i = 0; i < 14; i++) {
        const a = -Math.PI / 2 + (i - 6.5) * .16, x0 = cx + R * .98 * Math.cos(a), y0 = cy + R * .98 * Math.sin(a);
        const sway = Math.sin(ph * 2 + i * .8) * R * .06, len = R * (.22 + .06 * Math.sin(ph + i));
        const x1 = x0 + Math.cos(a) * len + sway, y1 = y0 + Math.sin(a) * len;
        ctx.strokeStyle = col(mix(e.tint, WHITE, .3), .8); ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.quadraticCurveTo((x0 + x1) / 2 + sway, (y0 + y1) / 2, x1, y1); ctx.stroke();
        ctx.globalAlpha = .8; ctx.drawImage(e.spark, x1 - 4, y1 - 4, 8, 8); ctx.globalAlpha = 1;
      }
    }
  };

  function drawCell(ctx, w, h, t, e) {
    const ph = TAU * t / LOOP, cx = w / 2, cy = h / 2, R = Math.min(w, h) * .41;
    ctx.clearRect(0, 0, w, h);
    const radius = a => R * (1 + .04 * Math.sin(2 * a + 2 * ph) + .024 * Math.sin(3 * a - 3 * ph + 1.3) + .014 * Math.sin(5 * a + 4 * ph));
    const outline = k => { ctx.beginPath(); for (let i = 0; i <= 180; i++) { const a = i / 180 * TAU, r = radius(a) * k; i ? ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a)) : ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a)); } ctx.closePath(); };
    // Outer halo and a slow ring of fine light particles.
    const halo = ctx.createRadialGradient(cx, cy, R * .7, cx, cy, R * 1.35);
    halo.addColorStop(0, col(e.tint, .2 + .08 * Math.sin(ph * 4))); halo.addColorStop(1, col(e.tint, 0));
    ctx.fillStyle = halo; ctx.fillRect(0, 0, w, h);
    for (const m of e.motes) {
      const a = m.a + m.d * ph, r = R * m.r;
      ctx.globalAlpha = .4 + .35 * Math.sin(ph * 4 + m.a);
      ctx.drawImage(e.spark, cx + r * Math.cos(a) - m.s, cy + r * Math.sin(a) - m.s, m.s * 2, m.s * 2);
    }
    ctx.globalAlpha = 1;
    // Translucent body, brighter towards the membrane.
    ctx.save(); outline(1); ctx.clip();
    const body = ctx.createRadialGradient(cx, cy, R * .2, cx, cy, R * 1.05);
    body.addColorStop(0, col(e.tint, .05)); body.addColorStop(.7, col(e.tint, .12)); body.addColorStop(1, col(e.tint, .32));
    ctx.fillStyle = body; ctx.fillRect(cx - R * 1.2, cy - R * 1.2, R * 2.4, R * 2.4);
    for (const s of e.specks) {
      const a = s.a + .06 * Math.sin(s.k * ph + s.w), r = R * (s.r + .02 * Math.cos(s.k * ph + s.w));
      ctx.fillStyle = col(mix(e.tint, WHITE, .5), .35); ctx.beginPath(); ctx.arc(cx + r * Math.cos(a), cy + r * Math.sin(a), s.s, 0, TAU); ctx.fill();
    }
    for (const v of e.organelles) {
      const a = v.a + v.d * ph, r = R * v.r, size = R * v.s;
      ctx.globalAlpha = .85; ctx.drawImage(e.vesicle, cx + r * Math.cos(a) - size / 2, cy + r * Math.sin(a) - size / 2, size, size);
    }
    ctx.globalAlpha = 1;
    if (e.art !== 'senescent' && e.art !== 'follicle') INNER[e.art](ctx, cx, cy, R, ph, e);
    ctx.restore();
    // Membrane: glowing bilayer rim in the product colour.
    ctx.save(); ctx.shadowColor = col(e.tint, .9); ctx.shadowBlur = R * .09;
    ctx.strokeStyle = col(mix(e.tint, WHITE, .15), .95); ctx.lineWidth = Math.max(2, R * .016); outline(1); ctx.stroke(); ctx.restore();
    ctx.strokeStyle = col(WHITE, .45); ctx.lineWidth = 1; outline(.965); ctx.stroke();
    if (e.art === 'senescent' || e.art === 'follicle') INNER[e.art](ctx, cx, cy, R, ph, e);
  }

  // One shared loop for every running instance.
  const running = new Set();
  let frame = 0, last = 0;
  function loop(now) {
    frame = 0;
    if (!running.size) return;
    if (!last || now - last >= 1000 / FPS - 2) {
      const dt = last ? Math.min((now - last) / 1000, .1) : 0; last = now;
      running.forEach(el => { el.time = (el.time + dt) % LOOP; el.render(); });
    }
    frame = requestAnimationFrame(loop);
  }
  const kick = () => { if (!frame && running.size) { last = 0; frame = requestAnimationFrame(loop); } };

  customElements.define('masi-pdp-r58-cell', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.canvas = this.querySelector('canvas'); this.ctx = this.canvas?.getContext('2d');
      if (!this.ctx) return;
      this.ready = true;
      const art = this.dataset.art in INNER ? this.dataset.art : 'nad';
      const tint = rgb(this.dataset.accent || getComputedStyle(this).getPropertyValue('--r51-accent'));
      const r = rng([...art].reduce((a, c) => a + c.charCodeAt(0), 11));
      this.env = {
        art, tint, spark: glowDot(64, tint), vesicle: pearl(64, mix(tint, WHITE, .15)),
        items: Array.from({ length: 18 }, () => ({ o: r(), a: r() * TAU, z: 5 + r() * 6 })),
        specks: Array.from({ length: 90 }, () => ({ a: r() * TAU, r: .55 + Math.sqrt(r()) * .42, k: 2 + Math.floor(r() * 2), w: r() * TAU, s: .7 + r() * 1.1 })),
        organelles: Array.from({ length: 8 }, (_, i) => ({ a: i / 8 * TAU + r() * .4, r: .7 + r() * .18, s: .1 + r() * .05, d: i % 2 ? 2 : -2 })),
        motes: Array.from({ length: 26 }, (_, i) => ({ a: r() * TAU, r: 1.1 + r() * .1, s: 2 + r() * 3, d: i % 3 ? 2 : -2 }))
      };
      this.time = 0;
      this.resizer = new ResizeObserver(() => this.resize()); this.resizer.observe(this);
      this.watcher = new IntersectionObserver(es => { this.visible = es.some(x => x.isIntersecting); this.update(); }, { rootMargin: '200px 0px' });
      this.watcher.observe(this);
      this.onChange = () => this.update();
      reduced.addEventListener('change', this.onChange); document.addEventListener('visibilitychange', this.onChange);
      this.resize();
    }
    resize() {
      const b = this.getBoundingClientRect(); if (!b.width || !b.height) return;
      const ratio = Math.min(devicePixelRatio || 1, innerWidth < 700 ? 1.5 : 2);
      this.w = b.width; this.h = b.height;
      this.canvas.width = Math.round(b.width * ratio); this.canvas.height = Math.round(b.height * ratio);
      this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0); this.render(); this.dataset.rendered = 'true';
    }
    render() { if (this.w) drawCell(this.ctx, this.w, this.h, this.time, this.env); }
    update() {
      const run = this.visible && !document.hidden && !reduced.matches;
      this.dataset.motion = run ? 'running' : 'still';
      if (run) { running.add(this); kick(); } else { running.delete(this); this.render(); }
    }
    disconnectedCallback() {
      running.delete(this); this.resizer?.disconnect(); this.watcher?.disconnect();
      reduced.removeEventListener('change', this.onChange); document.removeEventListener('visibilitychange', this.onChange); this.ready = false;
    }
  });

  // Journey entrances. A milestone is hidden before its entrance only when this script runs and motion is allowed.
  function showAll() {
    document.querySelectorAll('.r60-milestone').forEach(x => x.classList.add('is-in'));
    document.querySelectorAll('.r60-milestones').forEach(l => l.style.setProperty('--r60-fill', '1'));
  }
  function setupJourney() {
    const sections = document.querySelectorAll('.r60-journey');
    if (!sections.length || reduced.matches || !('IntersectionObserver' in window)) return;
    document.documentElement.classList.add('r60-anim');
    sections.forEach(section => {
      const items = [...section.querySelectorAll('.r60-milestone')], list = section.querySelector('.r60-milestones');
      const fill = () => list?.style.setProperty('--r60-fill', (items.filter(x => x.classList.contains('is-in')).length / items.length).toFixed(3));
      list?.style.setProperty('--r60-fill', '0');
      const io = new IntersectionObserver(entries => entries.forEach(x => {
        if (!x.isIntersecting) return;
        // The rail animates only from its first entrance, so the initial reset to 0 is instant.
        if (list) list.dataset.r60Filling = '';
        x.target.classList.add('is-in'); io.unobserve(x.target); fill();
      }), { rootMargin: '0px 0px -10% 0px', threshold: .15 });
      items.forEach(x => io.observe(x));
    });
  }
  reduced.addEventListener('change', () => { if (reduced.matches) showAll(); });

  const start = () => { ensureCss(); setupJourney(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
